import * as React from 'react';
import {
  FlashList,
  FlashListProps,
  ListRenderItemInfo,
} from '@shopify/flash-list';
import { Card } from 'components/Card';
import { theme } from 'lib/theme';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { Txt } from 'components/Txt';
import { CardRow } from 'components/CardRow';
import { useDraggableScroll } from 'hooks/useDraggableScroll';
import { ICONS } from 'components/Icon';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';
import { without } from 'lodash';
import { Button, ButtonTheme } from 'components/Button';

import { randomKey } from 'lib/riskbase';
import { useDebounce } from 'use-debounce';

const ColumnMargin = theme.paddingHorizontal;
const CheckboxColumnWidth = 36;

type GridColumn<ColumnDataIndex extends string> = {
  name: string;
  dataIndex: ColumnDataIndex;
  // width?: number;
  // minWidth?: number;
  maxWidth?: number;
  flex?: number;
  textAlign?: 'left' | 'center' | 'right';
} & (
  | {
      width: number;
      minWidth?: never;
    }
  | {
      width?: never;
      minWidth: number;
    }
);

export type GridProps<
  ColumnDataIndex extends string,
  ItemT extends string,
  OtherT extends Record<string, unknown>,
> = {
  data: NonNullable<FlashListProps<ItemT>['data']>;
  threshold?: number;
  onEndReached?: () => void;
  getRowData: (info: { id: ItemT; extraData: OtherT }) => null | {
    onPress: () => void;
    columns: Record<
      ColumnDataIndex,
      React.ReactElement | null | (() => React.ReactElement | null)
    >;
  };
  getItemType?: FlashListProps<ItemT>['getItemType'];
  extraData?: OtherT;
  ListFooterComponent?: FlashListProps<ItemT>['ListFooterComponent'];
  ListHeaderComponent?: FlashListProps<ItemT>['ListHeaderComponent'];
  ListHeaderComponentStyle?: FlashListProps<ItemT>['ListHeaderComponentStyle'];
  keyExtractor: FlashListProps<ItemT>['keyExtractor'];
  contentContainerStyle?: FlashListProps<ItemT>['contentContainerStyle'];
  ItemSeparatorComponent?: FlashListProps<ItemT>['ItemSeparatorComponent'];
  onScroll?: FlashListProps<ItemT>['onScroll'];
  // refreshControl?: FlashListProps<ItemT>['refreshControl'];
  columns: GridColumn<ColumnDataIndex>[];
  showSelectCheckboxes?: boolean;
  selectedIds?: ItemT[];
  onSelectionChange?: (value: ItemT[]) => void;
};

const GridHeaderRow = <
  ColumnDataIndex extends string,
  T extends string,
  OtherT extends Record<string, unknown>,
>({
  useColumnMinWidthAsWidth,
  columns,
  selectionState,
  onSelectionChange,
  showSelectCheckboxes,
}: {
  useColumnMinWidthAsWidth: boolean;
  columns: GridProps<ColumnDataIndex, T, OtherT>['columns'];
  selectionState: 'none' | 'all' | 'some';
  onSelectionChange: (change: 'all' | 'none') => void;
  showSelectCheckboxes: boolean;
}): React.ReactElement | null => {
  return (
    <View
      style={{
        marginBottom: theme.paddingHorizontalHalf,
        backgroundColor: theme.colors.background,
      }}
    >
      <Card
        style={{
          marginBottom: 0,
          marginTop: 0,
        }}
        withShadow={false}
      >
        <CardRow
          style={{
            backgroundColor: theme.colors.background90,
          }}
        >
          <ViewWithSeparators
            horizontal
            style={{ alignItems: 'center' }}
            separatorMargin={theme.paddingHorizontal}
          >
            {showSelectCheckboxes && (
              <View
                key="checkbox"
                style={{
                  width: CheckboxColumnWidth,
                  alignItems: 'center',
                  marginVertical: -12,
                }}
              >
                <ListSelectAllCheckbox
                  onSelectAll={() => onSelectionChange('all')}
                  onSelectNone={() => onSelectionChange('none')}
                  selectionState={
                    selectionState && selectionState !== 'none'
                      ? selectionState === 'some'
                        ? 'some'
                        : 'all'
                      : 'none'
                  }
                  hideMenu
                  style={{
                    flex: 1,
                  }}
                />
              </View>
            )}
            {columns.map((column) => {
              return (
                <View
                  key={`${column.dataIndex}-${column.width || column.minWidth || 0}`}
                  style={{
                    minWidth: column.minWidth,
                    width: column.minWidth
                      ? useColumnMinWidthAsWidth
                        ? column.minWidth
                        : undefined
                      : column.width,
                    maxWidth: column.maxWidth,
                    flex:
                      column.minWidth && !useColumnMinWidthAsWidth
                        ? column.flex || 1
                        : undefined,
                  }}
                >
                  <Txt
                    weight="bold"
                    style={
                      column.textAlign
                        ? { textAlign: column.textAlign }
                        : undefined
                    }
                  >
                    {column.name}
                  </Txt>
                </View>
              );
            })}
          </ViewWithSeparators>
        </CardRow>
      </Card>
    </View>
  );
};

const GridHeaderRowMemo = React.memo(GridHeaderRow);

const GridRow = <
  ColumnDataIndex extends string,
  T extends string,
  OtherT extends Record<string, unknown>,
>({
  id,
  extraData,
  columns,
  getRowData,
  selected,
  onToggleSelect,
  showSelectCheckboxes,
}: {
  id: T;
  index: number;
  extraData: OtherT;
  columns: GridProps<ColumnDataIndex, T, OtherT>['columns'];
  getRowData: GridProps<ColumnDataIndex, T, OtherT>['getRowData'];
  selected: boolean;
  onToggleSelect: (select: boolean, id: T) => void;
  showSelectCheckboxes: boolean;
}): React.ReactElement | null => {
  const renderedData = getRowData({ id, extraData });
  return React.useMemo(() => {
    if (!renderedData) {
      return null;
    }
    return (
      <Card
        style={{
          marginBottom: theme.paddingHorizontalHalf,
        }}
      >
        <CardRow onPress={renderedData.onPress}>
          <ViewWithSeparators
            horizontal
            style={{ alignItems: 'center' }}
            separatorMargin={theme.paddingHorizontal}
          >
            {showSelectCheckboxes && (
              <View
                key="checkbox"
                style={{
                  width: CheckboxColumnWidth,
                }}
              >
                <Pressable
                  style={{
                    position: 'absolute',
                    left: -12,
                    right: -12,
                    top: -19,
                    bottom: -17,
                  }}
                  onPress={() => onToggleSelect(!selected, id)}
                />
                <Button
                  style={{
                    // width: 36,
                    // height: 36,
                    justifyContent: 'center',
                    alignSelf: 'center',
                  }}
                  icon={selected ? ICONS.SquareCheckSolid : ICONS.SquareLight}
                  buttonTheme={ButtonTheme.themeless}
                  onPress={() => onToggleSelect(!selected, id)}
                  accessibilityHint={selected ? 'Deselects row' : 'Selects row'}
                />
              </View>
            )}
            {columns.map((column) => {
              const renderer = renderedData.columns[column.dataIndex];
              return (
                <View
                  key={`${column.dataIndex}-${column.width || column.minWidth || 0}`}
                  style={{
                    minWidth: column.minWidth,
                    width: column.minWidth
                      ? extraData.useColumnMinWidthAsWidth
                        ? column.minWidth
                        : undefined
                      : column.width,
                    maxWidth: column.maxWidth,
                    flex:
                      column.minWidth && !extraData.useColumnMinWidthAsWidth
                        ? column.flex || 1
                        : undefined,
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                >
                  {typeof renderer === 'function' ? renderer() : renderer}
                </View>
              );
            })}
          </ViewWithSeparators>
        </CardRow>
      </Card>
    );
  }, [
    renderedData,
    showSelectCheckboxes,
    selected,
    columns,
    onToggleSelect,
    id,
    extraData.useColumnMinWidthAsWidth,
  ]);
};

export const Grid = <
  ColumnDataIndex extends string,
  T extends string,
  OtherT extends Record<string, unknown>,
>({
  contentContainerStyle,
  data,
  ItemSeparatorComponent,
  keyExtractor,
  ListFooterComponent,
  ListHeaderComponent,
  ListHeaderComponentStyle,
  onEndReached,
  threshold = 0.9,
  extraData,
  onScroll,
  // refreshControl,
  columns,
  getRowData,
  showSelectCheckboxes,
  onSelectionChange,
  selectedIds,
  getItemType,
}: React.PropsWithChildren<GridProps<ColumnDataIndex, T, OtherT>>) => {
  const [viewWidth, setViewWidth] = React.useState(0);

  const selectionState: 'none' | 'all' | 'some' = !selectedIds?.length
    ? 'none'
    : data.length === selectedIds.length
      ? 'all'
      : 'some';

  const dataMod = React.useMemo(() => ['header' as T, ...data], [data]);

  const { refs } = useDraggableScroll<ScrollView>({
    cursor: 'grab',
  });

  const calculatedWidth = React.useMemo(
    () =>
      columns?.reduce(
        (w, c) =>
          showSelectCheckboxes
            ? w + CheckboxColumnWidth
            : w + (c.width || c.minWidth || 0),
        ColumnMargin * (columns.length * 2 + 2),
      ),
    [columns, showSelectCheckboxes],
  );

  const gridWidth = Math.max(calculatedWidth, viewWidth);
  const useColumnMinWidthAsWidth = calculatedWidth > viewWidth;
  const extraDataMod = React.useMemo<OtherT>(
    () =>
      ({
        ...(extraData || {}),
        useColumnMinWidthAsWidth,
        __selectedIdsChanged: `${selectedIds?.length || 0}-${randomKey()}`,
        __showSelectCheckboxes: showSelectCheckboxes,
      }) as unknown as OtherT,
    [extraData, useColumnMinWidthAsWidth, selectedIds, showSelectCheckboxes],
  );

  const handleSelectAllChanged = React.useCallback(
    (change: 'all' | 'none') => {
      onSelectionChange?.(change === 'all' ? [...data] : []);
    },
    [data, onSelectionChange],
  );

  const selectedIdsRef = React.useRef<T[]>([]);
  selectedIdsRef.current = selectedIds || [];
  const handleToggleSelect = React.useCallback(
    (select: boolean, id: T) => {
      selectedIdsRef.current = select
        ? [...selectedIdsRef.current, id]
        : without(selectedIdsRef.current, id);
      onSelectionChange?.(selectedIdsRef.current);
    },
    [onSelectionChange],
  );

  const handleLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { width },
      },
    }: LayoutChangeEvent) => setViewWidth(width),
    [],
  );

  const renderItem = React.useCallback(
    (info: ListRenderItemInfo<T>) => {
      return info.item === 'nothing' ? null : info.item === 'header' ? (
        <GridHeaderRowMemo
          columns={columns}
          useColumnMinWidthAsWidth={useColumnMinWidthAsWidth}
          onSelectionChange={handleSelectAllChanged}
          selectionState={selectionState}
          showSelectCheckboxes={!!showSelectCheckboxes}
        />
      ) : (
        <GridRow
          id={info.item}
          index={info.index}
          columns={columns}
          getRowData={getRowData}
          // Pass original extraData and not extraDataMod
          extraData={extraDataMod}
          // selected={!!selectedIds && selectedIds.includes(item.id)}
          selected={!!selectedIds && selectedIds.includes(info.item)}
          onToggleSelect={handleToggleSelect}
          showSelectCheckboxes={!!showSelectCheckboxes}
        />
      );
    },
    [
      extraDataMod,
      columns,
      useColumnMinWidthAsWidth,
      handleSelectAllChanged,
      selectionState,
      getRowData,
      selectedIds,
      handleToggleSelect,
      showSelectCheckboxes,
    ],
  );

  const [horizontalOffset, setHorizontalOffset] = useDebounce(0, 100, {
    maxWait: 100,
  });
  const handleHorizontalScroll = React.useCallback(
    ({
      nativeEvent: {
        contentOffset: { x },
      },
    }: NativeSyntheticEvent<NativeScrollEvent>) => setHorizontalOffset(x),
    [setHorizontalOffset],
  );

  return (
    <ScrollView
      ref={refs}
      horizontal
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      bounces={false}
      onLayout={handleLayout}
      onScroll={handleHorizontalScroll}
    >
      <FlashList
        stickyHeaderIndices={[0]}
        data={dataMod}
        extraData={extraDataMod}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemType={getItemType}
        contentContainerStyle={[contentContainerStyle, { width: gridWidth }]}
        showsVerticalScrollIndicator
        scrollIndicatorInsets={{
          right: gridWidth - viewWidth - horizontalOffset,
        }}
        // Events
        onEndReachedThreshold={threshold}
        onScroll={onScroll}
        onEndReached={onEndReached}
        // Components
        ListHeaderComponentStyle={ListHeaderComponentStyle}
        ListHeaderComponent={ListHeaderComponent}
        ListFooterComponent={ListFooterComponent}
        ItemSeparatorComponent={ItemSeparatorComponent}
      />
    </ScrollView>
  );
};
